import { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { CategoryLabels, ChallengeCategory } from "./types/types";

/**
 * Props for the CategorySelector component
 */
export interface CategorySelectorProps {
  /** Currently selected category */
  selectedCategory: ChallengeCategory;
  /** Callback function when category changes */
  onChange: (categoryId: ChallengeCategory) => void;
  /** Optional className for additional styling */
  className?: string;
  /** Button variant style */
  buttonVariant?: "default" | "secondary" | "ghost";
}

/**
 * CategorySelector component allows users to select a challenge category from a dropdown menu
 * @param props - CategorySelectorProps
 * @returns React component
 */
export function CategorySelector({
  selectedCategory,
  onChange,
  className,
  buttonVariant = "default",
}: CategorySelectorProps) {
  const selectedItem = CategoryLabels[selectedCategory];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={buttonVariant}
          className={cn("w-full justify-between", className)}
        >
          {selectedItem || "Select Category"}
          <ChevronDown className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[200px] bg-card/90 backdrop-blur-md border-white/10">
        {Object.values(ChallengeCategory).map((category) => (
          <DropdownMenuItem
            key={category}
            className={cn(
              "flex items-center justify-between cursor-pointer transition-colors",
              selectedCategory === category && "bg-primary/20"
            )}
            onClick={() => onChange(category)}
          >
            {CategoryLabels[category]}
            {selectedCategory === category && <Check className="h-4 w-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
